import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const DRAWER_SECTION_ID = 'cart-drawer-section';

/** Cart updates (adds, quantity changes) whose server response hasn't arrived yet. */
let pendingCartUpdates = 0;

/**
 * Instant feedback on add to cart. The server takes 1.5-2s to add the item and render the
 * drawer, and Horizon waits for that before opening an empty drawer, so a first tap looked
 * like it did nothing. Open the drawer straight away and show a loading state (see
 * [data-updating] in rupanjali-cart-drawer-styles) until the update lands.
 */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
  const component = drawer?.querySelector('cart-drawer-component');
  if (!drawer || !component) return;

  pendingCartUpdates += 1;
  drawer.setAttribute('data-updating', '');
  const settle = () => {
    // cart-items-component morphs the new contents in just after the promise resolves;
    // keep the loader up a moment longer so "Your cart is empty" never flashes in between
    setTimeout(() => {
      pendingCartUpdates = Math.max(0, pendingCartUpdates - 1);
      if (pendingCartUpdates === 0) drawer.removeAttribute('data-updating');
    }, 250);
  };
  if (event.promise) event.promise.then(settle, settle);
  else settle();

  // Adds from a quick-add modal open after that modal closes (cart-drawer.js handles it)
  const fromModal = event.target instanceof Element && event.target.closest('dialog:modal');
  if (event.action === 'add' && component.hasAttribute('auto-open') && !drawer.isOpen && !fromModal) {
    drawer.open();
  }
});

/**
 * Keeps the drawer honest. Its contents are rendered with the page, so a page restored from
 * the back/forward cache, or a cart changed outside the theme (e.g. by GoKwik), can show an
 * empty or outdated cart while the header count says otherwise. On open (and on bfcache
 * restore) compare with the live cart and re-render the drawer when they differ.
 */
async function syncCartDrawer() {
  // An add/update in flight will refresh the drawer itself; checking now would only race it
  if (pendingCartUpdates > 0) return;

  const inner = document.querySelector('#cart-drawer .cart-drawer__inner');
  if (!(inner instanceof HTMLElement)) return;

  let cart;
  try {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    cart = await response.json();
  } catch {
    return;
  }

  const shown = Number(inner.dataset.cartItemCount);
  if (cart.item_count === shown || pendingCartUpdates > 0) return;

  await sectionRenderer.renderSection(DRAWER_SECTION_ID, {
    cache: false,
    mode: 'hydration',
    // An empty drawer page never loaded the summary's styles
    injectStylesheet: shown === 0,
  });

  const cartIcon = /** @type {any} */ (document.querySelector('cart-icon'));
  cartIcon?.renderCartBubble?.(cart.item_count, false);
}

document.getElementById('cart-drawer')?.addEventListener(DrawerOpenEvent.eventName, syncCartDrawer);
window.addEventListener('pageshow', (event) => {
  if (event.persisted) syncCartDrawer();
});

/**
 * One-tap "Add" on the cart drawer's Curated Bestsellers cards
 * (snippets/rupanjali-cart-recommendations.liquid).
 *
 * Adds the variant with the drawer section in the same request, then announces the
 * change the way product-form does, so cart-items-component morphs the drawer and the
 * header cart bubble updates.
 */
document.addEventListener('click', async (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-cart-reco-add]');
  if (!(button instanceof HTMLButtonElement) || button.disabled) return;

  const cartItems = button.closest('cart-items-component');
  const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
  const variantId = Number(button.dataset.cartRecoAdd);
  if (!variantId) return;

  button.disabled = true;
  button.classList.add('is-loading');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        ...(sectionId ? { sections: sectionId } : {}),
      }),
    });
    const result = await response.json();
    const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'cart-recommendations',
        itemCount: 1,
        sections: result.status ? undefined : result.sections,
        didError: Boolean(result.status),
      },
    });
  } catch (error) {
    deferred.reject(error);
  } finally {
    // On success the morph replaces this card; restore it in case it stays (error)
    button.disabled = false;
    button.classList.remove('is-loading');
  }
});
